import styled, { css } from 'styled-components';
import { customMedia, truncateWidth } from 'styles/utils';

import Button from 'components/Button';
import Heading from 'components/Heading';
import Paragraph from 'components/Paragraph';
import Input from 'components/Form/Input';
import { Avatar } from 'components';

const AVATAR_SIZE = '32px';
const CHAT_FOOTER_HEIGHT = '64px';
const CHAT_TOP_BAR_HEIGHT = '8.5px';

export const ChatViewportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: calc(100% - ${CHAT_TOP_BAR_HEIGHT});
  max-height: calc(100% - ${CHAT_FOOTER_HEIGHT});
  padding: 0;
`;

export const ChatContentWrapper = styled.div`
  height: 100%;
  padding-top: ${({ hasPaddingTop, theme }) =>
    hasPaddingTop ? theme.space[6] : 'none'};
  // max-height: 300px;
  overflow-y: auto;
`;

export const ButtonDeleteMessage = styled(Button)`
  display: none;
  position: absolute;
  top: 50%;
  right: ${props => props.theme.spacing.default};
  transform: translateY(-50%);
`;

export const ChatMessageStyled = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-gap: ${props => props.theme.spacing.small};
  width: 100%;
  margin: 0;
  padding: ${props => props.theme.spacing.default};

  opacity: ${props => (props.pending ? '50%' : '100%')};
  border-top: ${props =>
    props.index === 0 ? 'none' : `1px solid ${props.theme.color.wfGrey200}`};
  &:first-of-type {
    margin-top: auto;
  }
  background: ${({ theme, expanded, editable }) =>
    editable ? theme.color.wfGrey50 : expanded && theme.color.wfGrey100};
  &:hover {
    background: ${props => props.theme.color.wfGrey50};
  }
`;

export const AvatarStyled = styled(Avatar)`
  align-self: flex-start;
`;

export const SenderName = styled(Heading)`
  margin-top: 0;
  font-family: ${props => props.theme.font.family.primary};
  color: ${props => props.theme.color.wfBlack};
  > a {
    color: ${props => props.theme.color.wfBlack};
    &:hover {
      color: ${props => props.theme.color.primary};
    }
  }
`;

export const ChatMessageIcon = styled.span`
  width: ${AVATAR_SIZE};
  height: ${AVATAR_SIZE};
  display: block;
  padding: ${props =>
    props.type !== 'annotation' &&
    props.type !== 'activity' &&
    props.theme.spacing.xsmall};
  border: ${props =>
    props.type !== 'annotation' &&
    props.type !== 'activity' &&
    '1px solid #000'};
  text-align: center;
  position: relative;
  ${props =>
    props.annotationNumber &&
    css`
      &::before {
        content: '${props.annotationNumber}';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: ${props.theme.font.size.small};
      }
    `}
  svg {
    path {
      fill: ${props =>
        props.active
          ? (props.highlighted === true || props.expanded) &&
            (props.targetType === 'part'
              ? props.theme.color.wfOrange
              : props.theme.color.wfPurple)
          : props.theme.color.wfGrey400};
    }
  }
  &:hover {
    cursor: ${props => props.active && 'pointer'};
    color: ${props => props.theme.color.wfBlack};
    svg {
      path {
        fill: ${props =>
          props.active
            ? props.targetType === 'part'
              ? props.theme.color.wfOrange
              : props.theme.color.wfPurple
            : props.theme.color.wfGrey400};
      }
    }
  }
`;

export const ReplyMessageIcon = styled.span`
  width: ${AVATAR_SIZE};
  height: ${AVATAR_SIZE};
  display: block;
  padding: ${props =>
    props.type !== 'annotation' && props.theme.spacing.xsmall};
  border: '1px solid ${props => props.theme.color.wfBlack}';
  text-align: center;
  position: relative;
  &:hover {
    cursor: pointer;
    color: ${props => props.theme.color.wfBlack};
  }
`;

export const ChatInputStyled = styled.form`
  padding: ${props => props.theme.spacing.small};
  background-color: ${props => props.theme.color.greyLightest};
  position: relative;
  bottom: 40px;
`;

export const ChatButtons = styled.div(({ theme, active }) => ({
  cursor: 'pointer',
  display: 'inline-flex',
  bottom: theme.space[3],
  right: theme.space[4],
  position: 'absolute',
  pointerEvents: !active ? 'none' : 'auto',
  color: active ? theme.color.primary : theme.color.wfGrey600,
}));

// TODO: alex - remove old styles after review
// export const ButtonChatInput = styled(Button)`
//   background-color: ${props => props.theme.color.wfBlack};
//   color: ${props => props.theme.color.wfWhite};
//   border: 1px solid ${props => props.theme.color.wfBlack};
//   &:not(:first-of-type) {
//     margin-left: ${props => props.theme.spacing.xsmall};
//   }
//   opacity: ${props => (props.disabled ? '0.1' : '1')};
// `;

export const ButtonChatInput = styled(Button)`
  color: ${props => props.theme.color.wfWhite};
`;

export const ParagraphStyled = styled(Paragraph)`
  color: ${props => props.theme.color.wfBlack};
  margin-top: ${props => props.theme.spacing.xsmall};
  ${props =>
    props.type === 'annotation' &&
    css`
      display: inline-block;
      vertical-align: text-bottom;
      margin-right: ${props.theme.spacing.small};
      font-size: ${props.theme.font.size.xsmall};
    `}
  ${props =>
    props.bodyCollapsible &&
    !props.isExpanded &&
    css`
      ${truncateWidth('170px')}
    `}
`;

export const InputStyled = styled(Input)`
  height: ${props => props.theme.spacing.xlarge};
  border-radius: ${props => props.theme.spacing.xlarge};
  padding: 0 ${props => props.theme.spacing.xlarge} 0
    ${props => props.theme.spacing.medium};

  &:focus {
    border-color: ${props => props.theme.color.wfBlack};
  }
  ${props =>
    props.isDisabled &&
    `
    cursor: not-allowed;
  `}
`;

export const ButtonCollapse = styled(Button)`
  align-self: flex-start;
`;

export const ChatDropdownStyled = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  cursor: pointer;
  &:hover {
    > h6 {
      color: ${props => props.theme.color.wfGrey800};
    }
  }
`;

export const ChatDropdownButtonStyled = styled.div`
  display: flex;
  flex-direction: row;
  user-select: none;
  justify-content: center;
  align-items: center;
  margin: 8px 0;
  padding: 0 16px;
  height: 24px;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.color.wfGrey200};
  color: ${props => props.theme.color.wfGrey400};
  background-color: ${props => props.theme.color.wfWhite};
  z-index: 2;

  svg {
    margin-left: 8px;
  }

  h6 {
    color: ${props => props.theme.color.wfGrey500};
  }

  ${customMedia.lessThan('medium')`
    height: 44px;
    border-radius: 22px;
  `};
  &:hover {
    color: ${props => props.theme.color.wfGrey800};
    h6 {
      color: ${props => props.theme.color.wfGrey800};
    }
  }
`;

export const ChatDropdownLine = styled.div`
  position: absolute;
  border-top: 1px solid ${props => props.theme.color.wfGrey200};
  width: 100%;
  height: 0px;
  margin-top: 20px;

  ${customMedia.lessThan('medium')`
    margin-top: 30px;
  `};
`;

export const BlockWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

export const EmptyChatWrapper = styled(BlockWrapper)`
  height: 100%;

  h3,
  h4 {
    max-width: 20rem;
  }
`;

export const InviteLink = styled.span`
  color: ${({ theme }) => theme.color.wfBlue};
  text-decoration: underline;
  cursor: pointer;
`;

export const SpinnerWrapper = styled.div`
  width: 100%;
  height: 80%;

  display: flex;
  justify-content: center;
  align-items: center;
`;
